import type { Lesson } from "@/core/types";
import { formelAufgabe } from "../formelaufgabe";

export const lektionDatumsfunktionen: Lesson = {
  id: "datumsfunktionen",
  title: "Datumsfunktionen: Alter, Fristen und Arbeitstage",
  summary: "Mit Datumswerten rechnen, ohne Kalender und Taschenrechner.",
  durationMinutes: 11,
  learningGoals: [
    "Mit HEUTE, JAHR und DATEDIF Zeitspannen berechnen",
    "Arbeitstage mit NETTOARBEITSTAGE und ARBEITSTAG zählen",
    "Monatsenden und Fristen mit EDATUM und MONATSENDE bestimmen",
  ],
  steps: [
    {
      id: "zeitspannen",
      title: "Zeitspannen: Alter und Betriebszugehörigkeit",
      explanation: [
        "Weil ein Datum in Excel eine Zahl ist (siehe Modul „Daten eingeben“), ergibt " +
          "=B2-A2 die Anzahl der Tage dazwischen. Für Jahre oder Monate gibt es DATEDIF: " +
          "=DATEDIF(A2;HEUTE();\"Y\") berechnet das Alter in vollen Jahren, \"M\" die " +
          "Monate, \"D\" die Tage.",
        "DATEDIF steht in keiner Funktionsliste und hat keine Eingabehilfe – ein " +
          "Überbleibsel aus alten Zeiten, das trotzdem zuverlässig funktioniert. Die " +
          "Alternative ist =BRTEILJAHRE(A2;HEUTE()) mit Nachkommastellen.",
        "HEUTE() liefert das aktuelle Datum und rechnet sich bei jedem Öffnen neu. Soll " +
          "ein Stichtag festgehalten werden, gehört er in eine eigene Zelle, auf die die " +
          "Formel verweist – Strg + . fügt das heutige Datum als festen Wert ein.",
      ],
      keyPoints: [
        "=DATEDIF(Start;Ende;\"Y\") – volle Jahre.",
        "HEUTE() rechnet sich neu, Strg + . fügt einen festen Wert ein.",
        "Stichtage in eine eigene Zelle.",
      ],
      task: {
        instruction:
          "Berechne aus einem Geburts- oder Eintrittsdatum per Formel die vollen Jahre bis heute.",
        hint: "=DATEDIF(A2;HEUTE();\"Y\") – A2 enthält ein echtes Datum, keinen Text.",
        check: formelAufgabe(
          [
            { label: "DATEDIF, BRTEILJAHRE oder JAHR berechnet eine Zeitspanne", funktionen: ["datedif", "brteiljahre", "jahr"] },
            { label: "Die Formel rechnet bis HEUTE() oder bis zu einem Stichtag", funktionen: ["heute", "datedif", "brteiljahre"] },
          ],
          {
            success: "Die Jahre zählen sich jetzt selbst weiter.",
            failure: "Die Zeitspanne wird noch nicht per Formel berechnet.",
            hint: "Ein #WERT! bedeutet meist, dass das Datum als Text in der Zelle steht.",
          },
        ),
      },
    },
    {
      id: "arbeitstage",
      title: "Arbeitstage und Fristen",
      explanation: [
        "NETTOARBEITSTAGE(Start;Ende;Feiertage) zählt die Werktage zwischen zwei Daten – " +
          "Wochenenden fallen weg, und im dritten Argument lässt sich ein Bereich mit " +
          "Feiertagen angeben. ARBEITSTAG(Start;Tage;Feiertage) geht den umgekehrten Weg: " +
          "Welches Datum liegt zehn Arbeitstage nach dem Eingang?",
        "Die Feiertage gehören auf ein eigenes Blatt, am besten als Tabelle. Dann genügt " +
          "es, sie einmal im Jahr zu pflegen, und jede Frist in der Mappe stimmt.",
        "Für Monatsfristen gibt es EDATUM(Start;Monate) – drei Monate nach dem 31. Januar " +
          "ist der 30. April – und MONATSENDE(Start;Monate) für „zum Ende des Folgemonats“.",
      ],
      keyPoints: [
        "NETTOARBEITSTAGE zählt, ARBEITSTAG rechnet eine Frist.",
        "Feiertage als Tabelle auf eigenem Blatt.",
        "EDATUM und MONATSENDE für Monatsfristen.",
      ],
      task: {
        instruction: "Berechne mit NETTOARBEITSTAGE oder ARBEITSTAG eine Frist oder eine Anzahl von Arbeitstagen.",
        hint: "=ARBEITSTAG(A2;10) liefert das Datum zehn Arbeitstage nach A2 – als Datum formatieren.",
        check: formelAufgabe(
          [{ label: "NETTOARBEITSTAGE oder ARBEITSTAG wird verwendet", funktionen: ["nettoarbeitstage", "arbeitstag"] }],
          {
            success: "Wochenenden rechnet Excel jetzt selbst heraus.",
            failure: "Es gibt noch keine Arbeitstagsformel.",
            hint: "Zeigt die Zelle eine fünfstellige Zahl, fehlt nur das Datumsformat.",
          },
        ),
      },
    },
  ],
};
